<template>
  <div class="flex flex-col h-full">
    <div class="flex flex-col gap-4 px-4 flex-1 overflow-y-auto">
      <div class="flex flex-col gap-1 w-full">
        <span class="font-medium">שם האירוע</span>
        <InputText v-model="title" placeholder="שם האירוע" type="text" class="w-full" />
      </div>

      <div class="w-full flex gap-3">
        <div class="flex flex-col gap-1 w-full">
          <span class="font-medium">שעת התחלה</span>
          <MobileTimePicker
            v-model="from"
            is24Hour
            placeholder="בחרו שעה"
          />
        </div>

        <div class="flex flex-col gap-1 w-full">
          <span class="font-medium">שעת סיום</span>
          <MobileTimePicker
            v-model="to"
            is24Hour
            placeholder="בחרו שעה"
          />
        </div>
      </div>

      <small v-if="invalidRange" class="text-red-500">שעת הסיום חייבת להיות אחרי שעת ההתחלה</small>
    </div>

    <div class="bg-white shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.05)] mt-auto">
      <drawer-footer
        class="px-4"
        approve-label="עדכון אירוע"
        cancel-label="חזור"
        :approveDisable="!isValid"
        :approveLoading="loading"
        @approve-click="save"
        @cancel-click="$emit('close')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import { useToast } from 'vue-toastification';
import { AppointmentsApi } from '@app/api/admin';
import { trackEvent } from '@app/composables/posthog';
import InputText from 'primevue/inputtext';

const props = defineProps<{
  apid: string;
  title?: string;
  // "yyyy-MM-dd HH:mm" strings from the calendar event.
  start: string;
  end: string;
}>();

const emit = defineEmits(['close', 'complete']);
const toast = useToast();

const parse = (value: string): Date =>
  DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm').toJSDate();

const from = ref<Date>(parse(props.start));
const to = ref<Date>(parse(props.end));
const title = ref(props.title ?? '');
const loading = ref(false);

// Decimal hour (e.g. 10.5 for 10:30) — matches how manual events store timeFrom/timeTo.
const toDecimal = (d: Date) => {
  const dt = DateTime.fromJSDate(d);
  return dt.hour + dt.minute / 60;
};

const invalidRange = computed(() => !!from.value && !!to.value && toDecimal(to.value) <= toDecimal(from.value));
// Clearing an existing title isn't allowed (the calendar needs a label); untitled legacy events can stay untitled.
const titleValid = computed(() => !!title.value.trim() || !props.title?.trim());
const isValid = computed(() => titleValid.value && !!from.value && !!to.value && !invalidRange.value);

const save = async () => {
  if (!isValid.value) return;
  loading.value = true;
  try {
    const newTitle = title.value.trim();
    const titleChanged = !!newTitle && newTitle !== (props.title ?? '').trim();
    trackEvent('manual_event_times_edited', { titleChanged });
    const dateISO = DateTime.fromFormat(props.start, 'yyyy-MM-dd HH:mm').toISODate() ?? undefined;
    await AppointmentsApi.editManualEventTimes(props.apid, toDecimal(from.value), toDecimal(to.value), dateISO, titleChanged ? newTitle : undefined);
    toast.success('האירוע עודכן בהצלחה 😄');
    emit('complete');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לעדכן את האירוע, אנא נסו שוב');
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped lang="scss">
</style>
